import React, { useEffect, useMemo } from 'react'
import { useApiFormProviderOrganization } from '../../../../../api-new/state/auth'
import { ModalDialog, useModalActions } from '../../../../../shared/component/modal-dialog'
import { FormNew } from '../../../../../shared/form-new/form'
import { FormNewText } from '../../../../../shared/form-new/form-text'
import { FormNewTags } from '../../../../../shared/form-new/form-tags'
import { FormNewButtonSubmit } from '../../../../../shared/form-new/form-button-submit'

export const PROVIDER_ORGANIZATION_RULE_MODAL = 'provider-organization-rule-modal'

// The rule in words, wherever a link's rule is shown: "When groups is
// staff", "When groups is one of staff, admins", "When email ends with
// @fuzion.com", and "Every sign-in" for a link with no rule (it joins
// unconditionally -- see rapid-api-auth's userlogin.OrgRule / ParseOrgRule).
// A person matches when their claim holds any of the values; `match` is "is"
// (the default, also when absent) or "ends_with", and neither is
// case-sensitive.
export const ENDS_WITH = 'ends_with'

// "groups is staff" / "email ends with one of @a.com, @b.com": the clause
// the label and the workspace's rule sentence share.
export const providerOrganizationRuleClause = matchData => {
  const verb = matchData.match === ENDS_WITH ? 'ends with' : 'is'
  const [only, ...more] = matchData.values
  return more.length ? `${matchData.claim} ${verb} one of ${matchData.values.join(', ')}` : `${matchData.claim} ${verb} ${only}`
}

export const providerOrganizationRuleLabel = matchData => {
  if (!matchData || !matchData.claim || !(matchData.values || []).length) return 'Every sign-in'
  return `When ${providerOrganizationRuleClause(matchData)}`
}

// Edit modal for one provider_organizations link's claim rule (round one +
// Task 11/14): a claim name plus a values tag input (FormNewTags, reused
// from round one). PUT /provider-organization/:providerID/:organizationID
// {matchData: {claim, values}} sets the rule; clearing both fields sends
// {matchData: null}, which removes it and makes the link admit every
// sign-in (the catch-all case for a shared connection).
//
// `link` is null when no row's rule is being edited -- the modal stays
// mounted with a fixed target (see provider-organization-list.js, which
// renders one instance outside the grid) and only shows once a row sets it.
export const ProviderOrganizationRuleModal = ({ link, enabled, onSaved, onCancel }) => {
  const apiFormProviderOrganization = useApiFormProviderOrganization()

  const modalActions = useModalActions(PROVIDER_ORGANIZATION_RULE_MODAL)

  useEffect(
    () => { if (link) modalActions.show() },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [link]
  )

  const state = useMemo(
    () => ({
      claim: (link && link.matchData && link.matchData.claim) || '',
      values: (link && link.matchData && link.matchData.values) || []
    }),
    [link]
  )

  const handleHide = () => onCancel && onCancel()

  const handleSubmit = async data => {
    const claim = (data.claim || '').trim()
    const values = (data.values || []).filter(v => v)

    // The modal has no match control: an existing "ends with" rule keeps it.
    const match = link.matchData && link.matchData.match === ENDS_WITH ? { match: ENDS_WITH } : {}
    const matchData = claim && values.length ? { claim, values, ...match } : null

    const result = await apiFormProviderOrganization.put(`${link.providerID}/${link.organizationID}`, { matchData })
    onSaved && onSaved(result)
  }

  return (
    <ModalDialog target={PROVIDER_ORGANIZATION_RULE_MODAL} header={link ? `Claim rule for ${link.organizationName}` : 'Claim rule'} onHide={handleHide}>
      {link &&
        <FormNew state={state} enabled={enabled} onSubmit={handleSubmit}>
          <FormNewText name="claim" label="claim name" />
          <FormNewTags name="values" label="values" placeholder="add a value and press Enter" />
          <p className="text-muted small mx-1">
            Leave the rule empty to admit every sign-in through this connection. When a rule is set, people join this
            organization only if the named claim contains one of these values; keep one link without a rule as the
            catch-all for a shared connection.
          </p>
          <FormNewButtonSubmit text="save rule" />
        </FormNew>}
    </ModalDialog>
  )
}
